import { getFirestore, collection, addDoc, getDocs, getDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth, BLOG_DOC_NAME } from '../firebase/firebaseConfig';

// Initialize Firestore
const db = getFirestore();
const storage = getStorage();

// Blog post interface
export interface BlogPost {
  id?: string;
  title: string;
  subtitle: string;
  description: string;
  categories: string[];
  hashtags: string[];
  imageUrl: string;
  createdAt: any;
  createdBy: string;
  updatedAt: any;
}

// Create a new blog post
export const createBlogPost = async (blogData: FormData): Promise<{ success: boolean, id?: string, error?: string }> => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return { success: false, error: 'User not authenticated' };
    }
    
    // Extract data from FormData
    const title = blogData.get('title') as string;
    const subtitle = blogData.get('subtitle') as string;
    const description = blogData.get('description') as string;
    const categoriesStr = blogData.get('categories') as string;
    const categories = JSON.parse(categoriesStr);
    const hashtagsStr = blogData.get('hashtags') as string || '[]';
    const hashtags = JSON.parse(hashtagsStr);
    const imageUrl = blogData.get('imageUrl')  as string;
    

    
    // Create blog post document
    const blogPost: Omit<BlogPost, 'id'> = {
      title,
      subtitle,
      description,
      categories,
      hashtags,
      imageUrl,
      createdAt: serverTimestamp(),
      createdBy: currentUser.uid,
      updatedAt: serverTimestamp()
    };
    
    // Add to Firestore
    const docRef = await addDoc(collection(db, BLOG_DOC_NAME), blogPost);
    
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating blog post:', error);
    return { success: false, error: (error as Error).message };
  }
};

// Get all blog posts
export const getAllBlogPosts = async (): Promise<BlogPost[]> => {
  try {
    const q = query(collection(db, 'blogs'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as BlogPost));
  } catch (error) {
    console.error('Error getting blog posts:', error);
    return [];
  }
};

// Get a single blog post by ID
export const getBlogPostById = async (id: string): Promise<BlogPost | null> => {
  try {
    const docRef = doc(db, 'blogs', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as BlogPost;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting blog post:', error);
    return null;
  }
};

// Update a blog post
export const updateBlogPost = async (id: string, blogData: Partial<BlogPost>, newImage?: File): Promise<boolean> => {
  try {
    const docRef = doc(db, 'blogs', id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return false;
    }
    
    const updates: Partial<BlogPost> = {
      ...blogData,
      updatedAt: serverTimestamp()
    };
    
    // If there's a new image, upload it and update imageUrl
    if (newImage) {
      const oldBlogData = docSnap.data() as BlogPost;
      
      // Delete old image if it exists
      if (oldBlogData.imageUrl) {
        try {
          const oldImageRef = ref(storage, oldBlogData.imageUrl);
          await deleteObject(oldImageRef);
        } catch (error) {
          console.warn('Error deleting old image:', error);
        }
      }
      
      // Upload new image
      const imageRef = ref(storage, `blog-images/${Date.now()}-${newImage.name}`);
      await uploadBytes(imageRef, newImage);
      updates.imageUrl = await getDownloadURL(imageRef);
    }
    
    await updateDoc(docRef, updates);
    return true;
  } catch (error) {
    console.error('Error updating blog post:', error);
    return false;
  }
};

// Delete a blog post
export const deleteBlogPost = async (id: string): Promise<boolean> => {
  try {
    const docRef = doc(db, 'blogs', id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return false;
    }
    
    const blogData = docSnap.data() as BlogPost;
    
    // Delete image from storage if it exists
    if (blogData.imageUrl) {
      try {
        const imageRef = ref(storage, blogData.imageUrl);
        await deleteObject(imageRef);
      } catch (error) {
        console.warn('Error deleting image:', error);
      }
    }
    
    // Delete document
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error('Error deleting blog post:', error);
    return false;
  }
}; 